import {
  X,
  User,
  Target,
  Calendar,
  CheckCircle2,
  Clock,
  Edit,
  Trash2,
  AlertTriangle,
  TrendingUp,
  Flag,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { Goal } from '../../types';
import {
  getGoalStatusColor,
  getGoalPriorityColor,
  getGoalCategoryLabel,
} from '../../types';

interface GoalDetailModalProps {
  readonly goal: Goal | null;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onEdit: (goal: Goal) => void;
  readonly onDelete: (goal: Goal) => void;
  readonly onUpdateProgress: (goal: Goal) => void;
  readonly onMarkComplete: (goal: Goal) => void;
}

function getProgressBarColor(status: string): string {
  if (status === 'completed') return 'bg-green-500';
  if (status === 'at-risk') return 'bg-red-500';
  return 'bg-blue-500';
}

export function GoalDetailModal({
  goal,
  isOpen,
  onClose,
  onEdit,
  onDelete,
  onUpdateProgress,
  onMarkComplete,
}: GoalDetailModalProps) {
  if (!isOpen || !goal) return null;

  const statusColor = getGoalStatusColor(goal.status);
  const priorityColor = getGoalPriorityColor(goal.priority);
  const categoryLabel = getGoalCategoryLabel(goal.category);
  const progressBarColor = getProgressBarColor(goal.status);
  
  const targetDate = new Date(goal.targetDate);
  const isOverdue = goal.status !== 'completed' && goal.status !== 'cancelled' && targetDate < new Date();
  
  const formattedTargetDate = targetDate.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  const createdDate = new Date(goal.createdAt).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  const completedDate = goal.completedAt
    ? new Date(goal.completedAt).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    })
    : null;

  const completedMilestones = goal.milestones?.filter(m => m.completed).length ?? 0;
  const totalMilestones = goal.milestones?.length ?? 0;

  const canMarkComplete = goal.status === 'in-progress' && goal.progress >= 100;
  const canUpdateProgress = goal.status !== 'completed' && goal.status !== 'cancelled';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <button
        type="button"
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        onKeyDown={(e) => e.key === 'Escape' && onClose()}
        aria-label="Close modal"
      />
      <div className="relative z-10 w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-lg bg-white shadow-xl">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-white px-6 py-4">
          <h2 className="text-lg font-semibold">Goal Details</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1 hover:bg-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="rounded-full bg-primary/10 p-3">
                <Target className="h-8 w-8 text-primary" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900">{goal.title}</h3>
                <p className="text-gray-500">{goal.employeeName} • {goal.employeeRole}</p>
                <p className="text-sm text-gray-400">{goal.siteName}</p>
              </div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <span className={`rounded-full px-3 py-1 text-sm font-medium capitalize ${statusColor}`}>
                {goal.status.replace('-', ' ')}
              </span>
              <span className={`rounded-full px-3 py-1 text-sm font-medium capitalize ${priorityColor}`}>
                {goal.priority} priority
              </span>
            </div>
          </div>

          <div className="border-t pt-4">
            <p className="text-sm font-medium text-gray-500 mb-2">Description</p>
            <p className="text-gray-600 whitespace-pre-wrap">{goal.description}</p>
          </div>

          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-gray-500">Progress</p>
              <span className="text-lg font-semibold text-gray-900">{goal.progress}%</span>
            </div>
            <div className="h-3 w-full rounded-full bg-gray-100">
              <div
                className={`h-3 rounded-full transition-all ${progressBarColor}`}
                style={{ width: `${goal.progress}%` }}
              />
            </div>
          </div>

          <div className="border-t pt-4 grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-500 mb-1">Category</p>
              <div className="flex items-center gap-2">
                <Flag className="h-4 w-4 text-gray-400" />
                <span className="text-gray-900">{categoryLabel}</span>
              </div>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500 mb-1">Target Date</p>
              <div className={`flex items-center gap-2 ${isOverdue ? 'text-red-600' : ''}`}>
                {isOverdue ? (
                  <AlertTriangle className="h-4 w-4" />
                ) : (
                  <Calendar className="h-4 w-4 text-gray-400" />
                )}
                <span className={isOverdue ? 'text-red-600' : 'text-gray-900'}>
                  {formattedTargetDate}
                  {isOverdue && ' (Overdue)'}
                </span>
              </div>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500 mb-1">Assigned By</p>
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-gray-400" />
                <span className="text-gray-900">{goal.assignedByName}</span>
              </div>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500 mb-1">Created</p>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-gray-400" />
                <span className="text-gray-900">{createdDate}</span>
              </div>
            </div>
            {completedDate && (
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">Completed</p>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  <span className="text-gray-900">{completedDate}</span>
                </div>
              </div>
            )}
          </div>

          {goal.milestones && goal.milestones.length > 0 && (
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-medium text-gray-500">Milestones</p>
                <span className="text-sm text-gray-500">
                  {completedMilestones}/{totalMilestones} completed
                </span>
              </div>
              <div className="space-y-2">
                {goal.milestones.map((milestone) => (
                  <div
                    key={milestone.id}
                    className={`flex items-center gap-3 rounded-lg border p-3 ${
                      milestone.completed ? 'bg-green-50 border-green-200' : 'bg-white'
                    }`}
                  >
                    {milestone.completed ? (
                      <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                    ) : (
                      <div className="h-5 w-5 rounded-full border-2 border-gray-300 flex-shrink-0" />
                    )}
                    <div className="flex-1">
                      <p className={`text-sm ${milestone.completed ? 'text-green-700' : 'text-gray-700'}`}>
                        {milestone.title}
                      </p>
                      {milestone.completedAt && (
                        <p className="text-xs text-green-600">
                          Completed {new Date(milestone.completedAt).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 flex items-center justify-between border-t bg-gray-50 px-6 py-4">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onEdit(goal)}
            >
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="text-red-600 hover:bg-red-50 hover:text-red-700"
              onClick={() => onDelete(goal)}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          </div>
          <div className="flex items-center gap-2">
            {canUpdateProgress && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onUpdateProgress(goal)}
              >
                <TrendingUp className="mr-2 h-4 w-4" />
                Update Progress
              </Button>
            )}
            {canMarkComplete && (
              <Button
                size="sm"
                onClick={() => onMarkComplete(goal)}
              >
                <CheckCircle2 className="mr-2 h-4 w-4" />
                Mark Complete
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
